import { Phone, MapPin } from "lucide-react";
import { BRAND, LOGO_URL } from "../mock";
import { SEO } from "../components/SEO";
import VSLSection from "../components/VSLSection";
import UnlockOffer from "../components/UnlockOffer";
import ProblemAgitation from "../components/ProblemAgitation";
import CeramicEducation from "../components/CeramicEducation";
import CuratedReviews from "../components/CuratedReviews";
import ValueStack from "../components/ValueStack";
import PricingTiers from "../components/PricingTiers";
import FAQ from "../components/FAQ";
import UrgencyCTA from "../components/UrgencyCTA";

// Minimal chrome for a distraction-free VSL funnel. logo + phone only, no menu.
function FunnelTopBar() {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-[#120127]/90 backdrop-blur-xl border-b border-white/10"
      data-testid="funnel-topbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Jester Werks" className="w-8 h-8" />
          <span className="font-display text-lg md:text-xl text-white leading-none">
            JESTER WERKS
          </span>
        </a>
        <a
          href={`tel:${BRAND.phone}`}
          className="flex items-center gap-2 text-sm text-white hover:text-acid transition"
          data-testid="funnel-topbar-phone"
        >
          <Phone className="w-4 h-4" />
          <span className="hidden sm:inline font-label tracking-[0.15em]">{BRAND.phone}</span>
        </a>
      </div>
    </header>
  );
}

function FunnelFooter() {
  return (
    <footer className="bg-[#08010f] border-t border-white/5 py-10">
      <div className="max-w-4xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/50 font-label tracking-[0.15em]">
        <div className="flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5" />
          <span>5156 Oscar Peterson Blvd, Mississauga, ON</span>
        </div>
        <div>© {new Date().getFullYear()} Jester Werks &middot; Ceramic Coating Studio</div>
      </div>
    </footer>
  );
}

const OFFER_FAQS = [
  {
    q: "Why is it $550 in October when normal ceramic coatings cost $1,500?",
    a: "October is when we build our winter content library before the shop books solid from November on. In exchange for the $950 discount we ask for 3 easy things: (1) drop the car off pre-washed, (2) leave an honest Google review after pickup, and (3) film two quick clips with us on pickup day. a short video testimonial plus a rolling shoot of your finished car. That is the entire deal. You still get the full 2-layer Gtechniq system, the paint correction, the 5-year written warranty, and the before-and-after report. Nothing skipped, nothing stripped.",
  },
  {
    q: "Why do I need ceramic coating specifically for Ontario winter?",
    a: "Between November and April, Mississauga and the GTA get hit with 140,000+ tonnes of road salt, plus magnesium chloride, sand, slush, and freeze-thaw cycles. Bare clearcoat pulls salt into every micro-scratch, corrodes underneath and fades permanently by spring. A proper 2-layer Gtechniq coating creates a sacrificial 9H barrier so the salt sits on the coating, not your paint. Slush and ice slide off. Wash time drops from an hour to ten minutes. Come April your car looks like it did in October.",
  },
  {
    q: "What does the $550 actually include?",
    a: "Full decontamination prep, 1-stage paint correction (swirl and scratch removal), Gtechniq Crystal Serum Light 9H base coat, Gtechniq EXO v5 hydrophobic top coat, free Gtechniq window coating ($150 value) for rain and snow visibility, documented before-and-after photo report, written 5-year manufacturer warranty and free annual maintenance inspections.",
  },
  {
    q: "How long does it take? Will I have my car before the first snow?",
    a: "2 to 4 days depending on the size and paint condition of your car. Book by mid-November and you will have your car back before the first proper salt storm. After the first plow drops, we stop taking October-rate bookings until spring.",
  },
  {
    q: "Is my car eligible?",
    a: "Most vehicles qualify. Exotics, supercars, unique builds, luxury SUVs, EVs and any car with a good-looking finish get priority since they perform best on our channels. Text us a photo and we will tell you within an hour.",
  },
  {
    q: "Can I still book if the October spots fill up?",
    a: "Early-November spots open on a rolling basis at the same $550 rate. Once we hit our winter content quota (usually mid-November), the price returns to $1,500 until spring. Text us to reserve early or get on the December waitlist.",
  },
  {
    q: "Do you offer pickup and delivery in winter?",
    a: "Yes. Complimentary pickup and delivery anywhere in Mississauga, Vaughan, Toronto, Oakville, Etobicoke and the GTA within 25 km on the $550 package. In snow conditions we cover it with a flatbed at no extra charge.",
  },
];

export default function Offer() {
  return (
    <div className="grain bg-jester-deep text-white min-h-screen">
      <SEO
        title="Ceramic Coating Mississauga. $550 Offer | Jester Werks"
        description="Our $550 Gtechniq ceramic coating offer for Mississauga & the GTA. 2-layer coating, paint correction, free window coating, 5-year warranty. Limited to 12 vehicles a month."
        canonical={`${BRAND.url}/offer`}
        noindex={true}
      />
      <FunnelTopBar />

      {/* Hook headline */}
      <section
        className="relative pt-28 md:pt-36 pb-10 md:pb-14 text-center overflow-hidden"
        data-testid="funnel-hook"
      >
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-jester-purple/20 blur-[140px]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-acid/40 bg-acid/8 backdrop-blur-sm mb-6">
            <span className="w-2 h-2 rounded-full bg-acid animate-pulse" />
            <span className="text-xs font-label uppercase tracking-[0.3em] text-acid">
              October in Mississauga. First snow is 6 weeks away.
            </span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.92]">
            Salt-proof your car<br />
            for <span className="text-acid">$550</span><br />
            before <span className="text-jester">the first freeze.</span>
          </h1>
          <p className="mt-7 text-white/80 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            You saw the ad. Ontario winter is on the doorstep. In 6 weeks
            your clearcoat is about to eat 5 months of road salt, slush and
            freeze-thaw damage. This is the coating that stops it. Hand-applied
            Gtechniq, locked in before the first plow hits your street.
          </p>
        </div>
      </section>

      {/* 1. VSL video + big $550 Book Now CTA */}
      <VSLSection />

      {/* 2. The exchange: $1,500 → $550 with 3 easy things */}
      <UnlockOffer />

      {/* 3. Problem / Agitation */}
      <ProblemAgitation />

      {/* 3. Solution */}
      <CeramicEducation />

      {/* 4. Social proof */}
      <CuratedReviews showHeading={true} />

      {/* 5. $550 offer + bonus stack */}
      <ValueStack />

      {/* 6. Pricing packages */}
      <PricingTiers city="Mississauga" />

      {/* 7. FAQ */}
      <FAQ items={OFFER_FAQS} />

      {/* 8. Urgency + final CTA */}
      <UrgencyCTA />

      <FunnelFooter />
    </div>
  );
}
